import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { aTexto, fechaHora } from '../lib/formato.js';
import { CLS_VERBO, partirAccion, txt, TXT_PERFIL, TXT_VERBO } from '../lib/enums.js';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Cargando, Error as AvisoError, Vacio } from '../piezas/Estados.jsx';

/**
 * La auditoria: quien hizo que, cuando y con que valores.
 *
 * Es de solo lectura, y no por omision: la bitacora es append-only en la base
 * —hay un trigger que rechaza UPDATE y DELETE— y la API no expone ruta que
 * escriba. Una bitacora que se puede corregir no es evidencia de nada.
 *
 * Lo que hace util a esta pantalla es el antes y el despues. Saber que
 * "Elena edito el contrato CTR-MX-2026-00001" no resuelve nada; lo que
 * resuelve es ver que el volumen paso de 120 a 90.
 */

interface Entrada {
  id: string;
  ocurrido_en: string;
  actor_nombre: string;
  actor_perfil: string;
  a_nombre_de: string | null;
  accion: string;
  entidad: string;
  folio: string | null;
  antes: Record<string, unknown> | null;
  despues: Record<string, unknown> | null;
  traza_id: string | null;
}

interface AccionConocida {
  accion: string;
  entidad: string;
  n: number;
}

const POR_PAGINA = 50;

export function Auditoria(): JSX.Element {
  const [filas, setFilas] = useState<Entrada[] | null>(null);
  const [acciones, setAcciones] = useState<AccionConocida[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [pagina, setPagina] = useState(0);
  const [accion, setAccion] = useState('');
  const [folio, setFolio] = useState('');
  const [desdeEl, setDesdeEl] = useState('');
  const [hastaEl, setHastaEl] = useState('');
  const [abierta, setAbierta] = useState<string | null>(null);

  const consulta = useMemo(() => {
    const p = new URLSearchParams();
    p.set('limite', String(POR_PAGINA));
    if (pagina > 0) p.set('desde', String(pagina * POR_PAGINA));
    if (accion) p.set('accion', accion);
    if (folio.trim()) p.set('folio', folio.trim());
    if (desdeEl) p.set('desde_el', desdeEl);
    if (hastaEl) p.set('hasta_el', hastaEl);
    return p.toString();
  }, [pagina, accion, folio, desdeEl, hastaEl]);

  useEffect(() => {
    let vigente = true;
    setFilas(null);
    setError(null);
    api
      .obtener<Entrada[]>(`/v1/auditoria?${consulta}`)
      .then(({ datos, meta }) => {
        if (!vigente) return;
        setFilas(datos);
        setTotal(meta.total ?? datos.length);
        /* Las acciones que EXISTEN las dice el servidor. Una lista escrita a
           mano se queda corta con la primera accion nueva que alguien
           registre, y entonces el filtro esconde justo lo que se busca. */
        const a = (meta as Record<string, unknown>)['acciones'];
        if (Array.isArray(a)) setAcciones(a as AccionConocida[]);
      })
      .catch((e: unknown) => {
        if (vigente) setError(e);
      });
    return () => {
      vigente = false;
    };
  }, [consulta]);

  const paginas = Math.ceil(total / POR_PAGINA);
  const hayFiltro = Boolean(accion || folio.trim() || desdeEl || hastaEl);

  return (
    <>
      <Pagehead />

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="grid r4" style={{ gap: '0 14px' }}>
          <div className="campo">
            <label htmlFor="f-accion">Accion</label>
            <select
              id="f-accion"
              value={accion}
              onChange={(ev) => {
                setAccion(ev.target.value);
                setPagina(0);
              }}
              data-prueba="filtro-accion"
            >
              <option value="">Todas</option>
              {acciones.map((a) => (
                <option key={a.accion} value={a.accion}>
                  {etiquetaAccion(a.accion)} ({a.n})
                </option>
              ))}
            </select>
          </div>

          <div className="campo">
            <label htmlFor="f-folio">Folio o clave</label>
            <input
              id="f-folio"
              value={folio}
              onChange={(ev) => {
                setFolio(ev.target.value);
                setPagina(0);
              }}
              placeholder="TKT-MX-2026-00014"
              data-prueba="filtro-folio"
            />
            <span className="ayuda">Completo, no por partes.</span>
          </div>

          <div className="campo">
            <label htmlFor="f-desde">Desde</label>
            <input
              id="f-desde"
              type="date"
              value={desdeEl}
              onChange={(ev) => {
                setDesdeEl(ev.target.value);
                setPagina(0);
              }}
              data-prueba="filtro-desde"
            />
          </div>

          <div className="campo">
            <label htmlFor="f-hasta">Hasta</label>
            <input
              id="f-hasta"
              type="date"
              value={hastaEl}
              onChange={(ev) => {
                setHastaEl(ev.target.value);
                setPagina(0);
              }}
            />
            <span className="ayuda">El dia se incluye completo.</span>
          </div>
        </div>

        <div className="fila">
          {hayFiltro && (
            <button
              type="button"
              className="btn sm ghost"
              onClick={() => {
                setAccion('');
                setFolio('');
                setDesdeEl('');
                setHastaEl('');
                setPagina(0);
              }}
            >
              Limpiar filtros
            </button>
          )}
          <span className="crece" />
          {total > 0 && (
            <span className="muted" style={{ fontSize: 12 }} data-prueba="total">
              {total} {total === 1 ? 'entrada' : 'entradas'}
            </span>
          )}
        </div>
      </div>

      {error !== null && <AvisoError error={error} />}

      <div className="card p0">
        {filas === null && error === null ? (
          <Cargando que="la bitacora" />
        ) : filas === null ? (
          <></>
        ) : filas.length === 0 ? (
          <Vacio
            mensaje={hayFiltro ? 'Nada coincide con el filtro' : 'La bitacora esta vacia'}
            ayuda={
              hayFiltro
                ? 'Prueba un rango de fechas mas amplio.'
                : 'Cada alta, edicion y baja queda registrada aqui.'
            }
            icono="🧾"
          />
        ) : (
          <table data-prueba="tabla-auditoria">
            <thead>
              <tr>
                <th>Cuando</th>
                <th>Quien</th>
                <th>Que</th>
                <th>Sobre</th>
                <th className="sec">Cambios</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((e) => {
                const { verbo } = partirAccion(e.accion);
                const difs = diferencias(e.antes, e.despues);
                const esta = abierta === e.id;

                return (
                  <tr key={e.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {fechaHora(e.ocurrido_en)}
                      {e.traza_id && (
                        <div className="muted mono" style={{ fontSize: 10.5 }}>
                          traza {e.traza_id.slice(0, 8)}
                        </div>
                      )}
                    </td>
                    <td>
                      {e.actor_nombre}
                      <div className="muted" style={{ fontSize: 11 }}>
                        {txt(TXT_PERFIL, e.actor_perfil)}
                      </div>
                      {/* Cuando alguien captura por cuenta de otro —el
                          despachador cierra por el IDC— quedan nombrados los
                          dos. Sin eso la bitacora dice que el despachador
                          estuvo en el sitio, y no estuvo. */}
                      {e.a_nombre_de && (
                        <div style={{ fontSize: 11, marginTop: 2 }}>
                          <span className="tag t-warm">a nombre de {e.a_nombre_de}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`tag ${CLS_VERBO[verbo] ?? 't-cold'}`}>
                        {etiquetaAccion(e.accion)}
                      </span>
                    </td>
                    <td>
                      {e.folio ? (
                        <span className="folio">{e.folio}</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                      <div className="muted" style={{ fontSize: 11 }}>
                        {e.entidad}
                      </div>
                    </td>
                    <td className="sec">
                      {difs.length === 0 ? (
                        <span className="muted">sin cambios de campo</span>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn sm ghost"
                            aria-expanded={esta}
                            onClick={() => setAbierta(esta ? null : e.id)}
                            data-prueba="ver-cambios"
                          >
                            {esta ? '▾' : '▸'} {difs.length}{' '}
                            {difs.length === 1 ? 'campo' : 'campos'}
                          </button>
                          {esta && (
                            <dl className="difs" style={{ marginTop: 6 }}>
                              {difs.map((d) => (
                                <div className="dif" key={d.campo}>
                                  <b>{d.campo}</b>
                                  <span>
                                    {d.antes !== undefined && (
                                      <span className="antes mono">{d.antes}</span>
                                    )}
                                    {d.antes !== undefined && d.despues !== undefined && ' → '}
                                    {d.despues !== undefined && (
                                      <span className="despues mono">{d.despues}</span>
                                    )}
                                  </span>
                                </div>
                              ))}
                            </dl>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {paginas > 1 && (
        <div className="fila" style={{ marginTop: 12, justifyContent: 'center' }}>
          <button
            type="button"
            className="btn sm"
            onClick={() => setPagina(pagina - 1)}
            disabled={pagina === 0}
          >
            ← Anterior
          </button>
          <span className="muted" style={{ fontSize: 12 }} data-prueba="pagina">
            Pagina {pagina + 1} de {paginas}
          </span>
          <button
            type="button"
            className="btn sm"
            onClick={() => setPagina(pagina + 1)}
            disabled={pagina + 1 >= paginas}
          >
            Siguiente →
          </button>
        </div>
      )}
    </>
  );
}

/** CLIENTE_ALTA -> "Alta de cliente". */
function etiquetaAccion(accion: string): string {
  const { entidad, verbo } = partirAccion(accion);
  const v = TXT_VERBO[verbo];
  const e = entidad.toLowerCase().replace(/_/g, ' ');
  /* Si el verbo no esta en el mapa se muestra la accion cruda en vez de
     inventarle texto: una accion nueva se ve fea pero se ve, y eso es
     preferible a que la bitacora la esconda. */
  return v ? `${v} de ${e}` : accion;
}

interface Dif {
  campo: string;
  antes: string | undefined;
  despues: string | undefined;
}

/**
 * Que cambio entre el antes y el despues.
 *
 * Solo los campos que DE VERDAD cambiaron. Un alta lista todo lo que se
 * guardo; una edicion lista nada mas lo que se movio, que es lo que se quiere
 * ver — una edicion que muestra los treinta campos obliga a buscar el cambio
 * a ojo, y asi es como se pasan por alto.
 */
function diferencias(
  antes: Record<string, unknown> | null,
  despues: Record<string, unknown> | null,
): Dif[] {
  const claves = new Set([...Object.keys(antes ?? {}), ...Object.keys(despues ?? {})]);
  const difs: Dif[] = [];

  for (const k of [...claves].sort()) {
    /* Los campos tecnicos y de rastro no son cambios de negocio: ensucian la
       lista y no dicen nada. `actualizado_en` cambia SIEMPRE. */
    if (k === 'id' || k.endsWith('_id') || k === 'actualizado_en' || k === 'creado_en') continue;

    const a = legible(antes?.[k]);
    const d = legible(despues?.[k]);
    if (a === d) continue;

    difs.push({
      campo: k.replace(/_/g, ' '),
      ...(a === undefined ? {} : { antes: a }),
      ...(d === undefined ? {} : { despues: d }),
    } as Dif);
  }
  return difs;
}

/** Un valor de JSON, comparable y legible. */
function legible(v: unknown): string | undefined {
  if (v === undefined) return undefined;
  if (v === null) return 'vacio';
  return aTexto(v);
}
